// image-source.ts — DE ONDE a imagem vem: direto da origem (TV) ou pelo nosso
// proxy (web). Decisão por CAPABILITY, nunca por marca.
//
// ===== A REGRA DE PRODUTO (dura) =====
// "O proxy é WEB-only; na TV o tráfego vai DIRETO — não rotear tráfego de TV
// pela nossa infra." Multiplicado por milhares de TVs × centenas de capas cada,
// proxiar imagem é banda e custo NOSSO pra servir um byte que a origem já serve.
// O mesmo princípio que já vale pro VÍDEO (ver stream-url.ts: na TV o modo é
// 'direct' e o ramo de proxy é inalcançável) passa a valer pra IMAGEM.
//
// ===== POR QUE O PROXY EXISTE (e por que não se aplica na TV) =====
//  1. MIXED CONTENT. Num navegador, uma página `https` NÃO carrega sub-recurso
//     `http` — e painéis Xtream frequentemente servem capa/logo em http. Sem
//     proxy, a imagem é bloqueada. Essa restrição só existe em CONTEXTO SEGURO
//     (página https). Na TV o app roda EMPACOTADO em `file://`, que não é
//     contexto seguro: carrega http e https livremente. (Confirmado no aparelho,
//     webOS/Chr53 — ver o relatório do fix.)
//  2. REDIMENSIONAMENTO. O proxy reescreve `?w=w342`. Só que o TMDB já serve a
//     variante pronta NA PRÓPRIA URL (`/t/p/w342/<path>`) — não precisa de
//     intermediário: basta trocar o segmento de tamanho, que é o que
//     `buildDirectImgUrl` faz com a MESMA função pura do backend
//     (`rewriteTmdbSize`, em @app-iptv/shared).
//  3. Cache do SW + ETag estável. Vale no web; na TV empacotada não há Service
//     Worker (file:// não registra SW), então esse ganho é nulo lá.
//
// ===== O QUE SOBRA PRA CAPA DO PAINEL XTREAM (não-TMDB) =====
// URL arbitrária, muitas vezes http, sem variante de tamanho: nada a otimizar,
// nada a reescrever. Vai INTACTA. Riscos conhecidos e MEDIDOS no device (não
// assumidos): host com cert https quebrado (o proxy tolerava via
// rejectUnauthorized:false) e hotlink protection por Referer. Se algum painel
// exigir mesmo o proxy, a decisão volta pra mesa — não se força por suposição.

import { detectPlatform } from '@app-iptv/device'
import { isPubliclyRoutableUrl, normalizeTmdbHost, rewriteTmdbSize } from '@app-iptv/shared'

import { isTvPlatform } from '../packaged-tv-scale'

/** O ambiente que a decisão enxerga. Injetável = função PURA e testável sem DOM
 *  (jsdom torna `window.location` unforgeable — não dá pra fingir `file:`). */
export interface ImageEnv {
  /** `window.location.protocol`: 'file:' | 'http:' | 'https:'. */
  protocol: string
  /** `detectPlatform()`: 'tizen' | 'webos' | 'androidtv' | 'firetv' | 'vidaa' |
   *  'web' | 'mobile-pwa' | ... */
  platform: string
}

/**
 * O app está EMPACOTADO? `file:` é o sinal — o WebView carregou o index.html do
 * diretório de instalação do .wgt/.ipk. Mesmo sinal que `isPackagedTv` e
 * `router-basename` já usam (reuso do detector, não um novo).
 *
 * Duas consequências, e as duas importam aqui:
 *  - NÃO é contexto seguro → sem bloqueio de mixed content → `http://` carrega.
 *  - É o nosso app de TV instalado → o tráfego dele não passa pela nossa infra.
 */
export function isPackagedApp(env: ImageEnv): boolean {
  return (env.protocol || '').toLowerCase() === 'file:'
}

/**
 * Usa o proxy de imagem? **Só no navegador.**
 *
 * Matriz (capability, não marca):
 *  - `file:` (qualquer plataforma) ......... DIRETO. Empacotado: sem mixed
 *    content e sem custo nosso. Cobre Tizen (.wgt), webOS (.ipk) e o WebView
 *    local do wrapper Android.
 *  - UA de TV em página NÃO-https (http) ... DIRETO. É TV (regra de produto) e
 *    a página não é contexto seguro, então `http://` do painel carrega igual.
 *  - Resto (navegador web/mobile, http ou https) ... PROXY. É lá que o mixed
 *    content morde de verdade, que o SW cacheia e que o redimensionamento
 *    centralizado ajuda. Não se regride o navegador.
 *
 * CASO DE FRONTEIRA, documentado de propósito: uma superfície de TV servida por
 * **https** (hipotético wrapper Capacitor com `androidScheme: 'https'`) cai no
 * ramo do proxy — porque ali o mixed content EXISTE e ir direto quebraria a capa
 * http do painel. Hoje não existe esse alvo (Tizen/webOS são file://); se um dia
 * existir, a saída é o app carregar por http/file, não reabrir o proxy pra TV.
 */
export function shouldProxyImages(env: ImageEnv): boolean {
  if (isPackagedApp(env)) return false
  const isSecurePage = (env.protocol || '').toLowerCase() === 'https:'
  if (isTvPlatform(env.platform) && !isSecurePage) return false
  return true
}

/** Lê o ambiente REAL (boot). Sem window (SSR/teste node) → assume web/proxy:
 *  é o caminho conservador, e sem DOM ninguém está renderizando <img> mesmo. */
export function readImageEnv(): ImageEnv {
  if (typeof window === 'undefined' || !window.location) {
    return { protocol: 'https:', platform: 'web' }
  }
  return { protocol: window.location.protocol, platform: detectPlatform() }
}

/** Atalho de runtime — o que o `<Poster>` chama. */
export function imagesUseProxy(): boolean {
  return shouldProxyImages(readImageEnv())
}

/**
 * Monta a URL DIRETA (sem intermediário) pra uma imagem, no tamanho pedido.
 *
 *  - **TMDB** (`image.tmdb.org/t/p/<size>/<path>`, e também as capas que o
 *    provider subiu apontando pro SITE `www.themoviedb.org`): normaliza pro CDN
 *    e troca o segmento de tamanho pelo `size` já decidido pelo caller (derivado
 *    da largura COMPUTADA do card × DPR × teto do tier — ver lib/card-metrics).
 *    Resultado: `https://image.tmdb.org/t/p/w342/abc.jpg`. Zero tráfego nosso.
 *  - **Qualquer outra URL** (capa/logo do painel Xtream): passa INTACTA. Não tem
 *    variante de tamanho pra pedir, e em `file://` a URL http carrega.
 *
 * As duas transformações são as MESMAS funções puras que o backend usa no
 * `/v1/proxy/img` (@app-iptv/shared) — cliente e servidor não podem divergir no
 * tamanho pedido, senão a mesma capa sai nítida num e borrada no outro.
 *
 * `size` null = passthrough (é o caso do `kind: 'logo'`).
 *
 * ===== O GUARD: SEM PROXY, NINGUÉM CHECA POR NÓS =====
 * Retorna `''` (string vazia = "não há URL carregável", tratado pelo <Poster>
 * como placeholder) quando a URL não é publicamente roteável. Ver
 * `isDirectImgUrlAllowed` abaixo.
 */
export function buildDirectImgUrl(src: string, size: string | null): string {
  if (!isDirectImgUrlAllowed(src)) return ''
  return rewriteTmdbSize(normalizeTmdbHost(src), size)
}

/**
 * A URL da capa pode ir DIRETO pro `<img>`?
 *
 * Enquanto TODA imagem passava pelo `/v1/proxy/img`, o backend aplicava
 * `validateUrl` antes de tocar na origem — e a URL da capa vem do painel Xtream
 * do PROVEDOR, ou seja, é entrada não-confiável. Ao mandar a TV buscar direto
 * (regra de produto: o proxy é web-only), esse guard saiu do caminho: quem faz o
 * request agora é o `<img>` do device, dentro da casa do usuário.
 *
 * O abuso concreto: um provedor hostil seta a capa como `http://192.168.0.1/` e
 * a TV do usuário passa a bater na LAN dele. A resposta é opaca num `<img>`
 * (não há exfiltração de conteúdo), mas `onload` vs `onerror` é um oráculo de
 * host/porta — dá pra mapear a rede local do usuário, capa por capa, e o
 * catálogo inteiro é controlado pelo provedor.
 *
 * A decisão é a mesma primitiva pura que o backend usa (`@app-iptv/shared`),
 * NÃO uma cópia: cliente e servidor divergirem sobre "isto é rede privada?" é
 * exatamente como a brecha volta. O guard do cliente é mais fraco que o do
 * servidor (não resolve DNS, logo não pega `evil.com → 192.168.0.1`) e isso está
 * documentado em url-safety.ts — ele fecha o vetor trivial e barato.
 *
 * NÃO restringe porta de propósito: painel Xtream serve capa em 8000/2095/25461
 * o tempo todo, e sumir com essas capas seria trocar um problema de segurança
 * por um problema de produto maior. O alvo (a LAN) é o que se fecha aqui.
 */
export function isDirectImgUrlAllowed(src: string): boolean {
  return isPubliclyRoutableUrl(src)
}
